/**
 * Home-gallery shaders — ported from the designer prototype
 * (library/THZ_Files-Dev-Heiko/.../Code_Example/script.js).
 *
 * Composites three square textures (front / back / next) into one full-screen
 * frame with a click/autoplay morph (u_morph) and the keyed "tail band" colour
 * effect (keyifyTailBands). It is a 3-band cousin of src/image-styler/shaders.js:
 * same noise + percentile-threshold keying, but with layered front-over-back
 * geometry and a scroll-driven colour takeover, ending in a flood to the page
 * background (uFadeTo) that leaves no trace of the photo behind the revealed
 * calendar.
 */

export const VERTEX_SRC = `
attribute vec2 a_pos;
varying vec2 v_uv;
void main(){
  v_uv = a_pos * 0.5 + 0.5;
  gl_Position = vec4(a_pos, 0.0, 1.0);
}
`

export const FRAGMENT_SRC = `
precision highp float;
varying vec2 v_uv;

uniform sampler2D u_tex0;
uniform sampler2D u_tex1;
uniform sampler2D u_tex2;

uniform vec2  u_res;
uniform int   u_frontIdx;
uniform int   u_backIdx;
uniform int   u_nextIdx;

uniform float u_morph;     // 0..1 click morph
uniform float uTime;
uniform vec2  uMouse;

uniform float uScale;
uniform float uSpeed;
uniform float uInfluence;

uniform float uTakeover;   // 0..1 (scroll-driven)
uniform float uWhiteFade;  // 0..1 (end fade: washes the whole frame toward uFadeTo)
uniform vec3  uFadeTo;     // page background the end state floods to — black in dark mode
uniform float u_keyAmount; // 1 = keyed colour bands on; 0 = raw photo (reduced-motion gallery)

uniform vec3  uTail0;
uniform vec3  uTail1;
uniform vec3  uTail2;

uniform int   uMode0;      // 0=key darks, 1=key lights
uniform int   uMode1;
uniform int   uMode2;

uniform float uSoftness;
uniform float uMouseRadius;

uniform float uNoiseDetail;
uniform float uNoiseContrast;
uniform float uNoiseRidge;

uniform vec3 uC0;
uniform vec3 uC1;
uniform vec3 uC2;
uniform vec3 uC3;
uniform vec3 uC4;

// ---- noise ----
float hash(vec2 p){
  p = fract(p*vec2(123.34,345.45));
  p += dot(p,p+34.345);
  return fract(p.x*p.y);
}
float noise(vec2 p){
  vec2 i=floor(p), f=fract(p);
  vec2 u=f*f*(3.0-2.0*f);
  float a=hash(i), b=hash(i+vec2(1,0)), c=hash(i+vec2(0,1)), d=hash(i+vec2(1,1));
  return mix(mix(a,b,u.x), mix(c,d,u.x), u.y);
}
float fbm(vec2 p){
  float v=0.0;
  float a=0.55;
  for(int i=0;i<3;i++){
    float n = noise(p);
    float w = mix(1.0, float(i)/5.0, clamp(uNoiseDetail, 0.0, 1.0));
    v += a * n * w;
    p *= 2.0;
    a *= 0.5;
  }
  return v;
}
float luma(vec3 c){ return dot(c, vec3(0.2126,0.7152,0.0722)); }
float contrast01(float x, float k){
  return clamp((x - 0.5) * k + 0.5, 0.0, 1.0);
}

vec3 tailByIdx(int idx){
  if(idx==0) return uTail0;
  if(idx==1) return uTail1;
  return uTail2;
}
int modeByIdx(int idx){
  if(idx==0) return uMode0;
  if(idx==1) return uMode1;
  return uMode2;
}
vec4 sampleByIdx(int idx, vec2 uv){
  if (idx==0) return texture2D(u_tex0, uv);
  if (idx==1) return texture2D(u_tex1, uv);
  return texture2D(u_tex2, uv);
}

void scaleRectAboutCenter(in vec2 sizeIn, in vec2 offIn, in float s,
                          out vec2 sizeOut, out vec2 offOut)
{
  vec2 center = offIn + 0.5 * sizeIn;
  sizeOut = sizeIn * s;
  offOut = center - 0.5 * sizeOut;
}
void uvInRect(in vec2 uv, in vec2 res, in vec2 size, in vec2 off,
              out vec2 tuv, out float inside)
{
  vec2 p = uv * res;
  vec2 q = (p - off) / size;
  inside = step(0.0, q.x) * step(0.0, q.y) * step(q.x, 1.0) * step(q.y, 1.0);
  tuv = q;
}

// Tail banding into 3 colors + takeover.
// Outside the tail region, keep original rgb.
vec3 keyifyTailBands(vec3 rgb, vec3 baseT, int mode){
  // Reduced-motion gallery sets u_keyAmount=0: return the raw photo and skip the
  // whole keying (incl. the fbm noise cost) — "the fancy composition without the shader".
  if (u_keyAmount < 0.5) return rgb;

  float lum = luma(rgb);

  // ----- richer motion: domain-warp + subtle swirl -----
  float timev = uTime * uSpeed;

  // flow field (2D) from fbm at slower scale
  vec2 flowP = v_uv * (uScale * 0.35) + vec2(timev * 0.25, -timev * 0.18);
  float fx = fbm(flowP + vec2(17.3, 9.2));
  float fy = fbm(flowP + vec2(-3.7, 12.9));
  vec2 flow = vec2(fx, fy) - 0.5;

  // subtle swirl around center — reuses the flow fbm samples (fx/fy) instead of a
  // 4th independent fbm call per layer (~25% less keying-noise cost, near-identical look)
  vec2 cc = v_uv - vec2(0.5);
  float ang = 0.35 * sin(timev * 0.35)
            + 0.20 * ((fx + fy) * 0.5 - 0.5);

  float cs = cos(ang), sn = sin(ang);
  vec2 rotUV = vec2(cc.x * cs - cc.y * sn, cc.x * sn + cc.y * cs) + vec2(0.5);

  // domain warp
  float warpAmt = 0.85;
  vec2 p = rotUV * uScale
         + flow * (warpAmt * uInfluence * 0.35)
         + vec2(timev * 0.12, -timev * 0.08);

  // sample fbm on warped coords
  float f = fbm(p);
  float ridged = 1.0 - abs(2.0*f - 1.0);
  float n = mix(f, ridged, clamp(uNoiseRidge, 0.0, 1.0));
  n = contrast01(n, uNoiseContrast);

  float d = distance(v_uv, uMouse);
  float m = smoothstep(uMouseRadius, 0.0, d);
  float edgeInfluence = mix(0.2, 2.2, m);

  float wobble = (n - 0.5) * uInfluence * 0.02 * edgeInfluence;
  vec3 t = baseT + vec3(wobble);

  // takeover spreads inner region outward (clamped so it doesn't flood the center)
  float tt = clamp(uTakeover, 0.0, 1.0);
  tt = min(tt, 0.55);
  tt = tt * tt * (3.0 - 2.0 * tt);

  if (mode == 0) {         // key DARKS
    t = mix(t, vec3(1.0), tt);
  } else {                  // key LIGHTS
    t = mix(t, vec3(0.0), tt);
  }

  // Bands keep their full palette colour throughout the scroll — the end-state wash
  // to the page background is applied ONCE to the finished frame in main() (see
  // uFadeTo), not per band, so the photo and the bands dissolve together.
  vec3 cA = uC4;
  vec3 cB = uC1;
  vec3 cC = uC2;

  float b1, b2, b3;

  if(mode == 0){
    float s1 = 1.0 - smoothstep(t.x, t.x + uSoftness, lum);
    float s2 = smoothstep(t.x, t.x + uSoftness, lum) * (1.0 - smoothstep(t.y, t.y + uSoftness, lum));
    float s3 = smoothstep(t.y, t.y + uSoftness, lum) * (1.0 - smoothstep(t.z, t.z + uSoftness, lum));
    b1 = s1; b2 = s2; b3 = s3;
  } else {
    float ml = 1.0 - lum;
    vec3 mt = vec3(1.0 - t.x, 1.0 - t.y, 1.0 - t.z);
    float s1 = 1.0 - smoothstep(mt.x, mt.x + uSoftness, ml);
    float s2 = smoothstep(mt.x, mt.x + uSoftness, ml) * (1.0 - smoothstep(mt.y, mt.y + uSoftness, ml));
    float s3 = smoothstep(mt.y, mt.y + uSoftness, ml) * (1.0 - smoothstep(mt.z, mt.z + uSoftness, ml));
    b1 = s1; b2 = s2; b3 = s3;
  }

  float mask = clamp(b1 + b2 + b3, 0.0, 1.0);
  vec3 bandCol = cA*b1 + cB*b2 + cC*b3;
  return mix(rgb, bandCol, mask);
}

vec4 over(vec4 dst, vec4 src){
  float a = src.a;
  return vec4(dst.rgb * (1.0 - a) + src.rgb * a, 1.0);
}

void main(){
  float t = clamp(u_morph, 0.0, 1.0);
  float s = smoothstep(0.0, 1.0, t);
  float sSafe = clamp(s, 0.001, 1.0);

  // Layout targets (all squares):
  // - FRONT: centered square (side = min(w,h))
  // - BACK:  full-width square (side = viewport width), vertically centered
  float sideFront = min(u_res.x, u_res.y);
  vec2  frontSqSize = vec2(sideFront, sideFront);
  vec2  frontSqOff  = 0.5 * (u_res - frontSqSize);

  // Cover the viewport in any orientation: size the back square to the LONGER
  // edge and centre it (using width alone left a centred square + bars on
  // portrait/mobile, where width is the shorter edge).
  float sideBack = max(u_res.x, u_res.y);
  vec2  backSqSize = vec2(sideBack, sideBack);
  vec2  backSqOff  = 0.5 * (u_res - backSqSize);

  // Back shrinks away as morph progresses
  vec2 backSize, backOff;
  scaleRectAboutCenter(backSqSize, backSqOff, 1.0 - sSafe, backSize, backOff);

  // Front morphs into the back square (smooth front->back movement)
  vec2 frontSize = mix(frontSqSize, backSqSize, s);
  vec2 frontOff  = mix(frontSqOff,  backSqOff,  s);

  // Next grows into the front square
  vec2 nextSize, nextOff;
  scaleRectAboutCenter(frontSqSize, frontSqOff, sSafe, nextSize, nextOff);

  int frontIdx = u_frontIdx;
  int backIdx  = u_backIdx;
  int nextIdx  = u_nextIdx;

  vec2 uvBack;  float inBack;
  vec2 uvFront; float inFront;
  vec2 uvNext;  float inNext;

  uvInRect(v_uv, u_res, backSize,  backOff,  uvBack,  inBack);
  uvInRect(v_uv, u_res, frontSize, frontOff, uvFront, inFront);
  uvInRect(v_uv, u_res, nextSize,  nextOff,  uvNext,  inNext);

  vec4 outc = vec4(0.0, 0.0, 0.0, 1.0);

  // Draw order: back -> front -> next (next fades in with alpha s)
  if(inBack > 0.5){
    vec3 rgb = sampleByIdx(backIdx, uvBack).rgb;
    rgb = keyifyTailBands(rgb, tailByIdx(backIdx), modeByIdx(backIdx));
    outc = over(outc, vec4(rgb, 1.0));
  }
  if(inFront > 0.5){
    vec3 rgb = sampleByIdx(frontIdx, uvFront).rgb;
    rgb = keyifyTailBands(rgb, tailByIdx(frontIdx), modeByIdx(frontIdx));
    outc = over(outc, vec4(rgb, 1.0));
  }
  if(inNext > 0.5){
    vec3 rgb = sampleByIdx(nextIdx, uvNext).rgb;
    rgb = keyifyTailBands(rgb, tailByIdx(nextIdx), modeByIdx(nextIdx));
    outc = over(outc, vec4(rgb, s));
  }

  // End state: wash the WHOLE composited frame to the page background as uWhiteFade
  // runs 0→1 over the scroll tail. At the end nothing of the photo survives — not the
  // pixels (they were already keyed away) and not its shapes, which the colour bands
  // were still tracing. The calendar is revealed onto a flat plate: the page
  // background, so white in light mode and black in dark mode (uFadeTo).
  gl_FragColor = vec4(mix(outc.rgb, uFadeTo, uWhiteFade), 1.0);
}
`
